import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { tenantPath } from "@/lib/tenant";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { usePageHeaderActions } from "../../components/PageHeaderActions";
import ResourceStatus from "../../components/ResourceStatus";
import { listJobs, readJob } from "./services";
import useJobResource from "./useJobResource";
import JobList from "./JobList";
import { canUseJobs, activeStatuses, isRecentJob, RECENT_JOB_MS } from "./access";
import { handlesUploadPage } from "./navigation";

export default function JobsWindow() {
  const { user } = useAuth();
  const { merchant } = useTenant();
  if (!merchant?.id || !canUseJobs(user)) return null;
  const scope = JSON.stringify([merchant.id, user.id, user.role, user.permissions, user.page_access,
    user.role_profile_id, user.role_profile_revision, user.allowed_pages]);
  return <Jobs key={scope} />;
}

export function Jobs() {
  const location = useLocation();
  const requested = new URLSearchParams(location.search).get("background_job") || "";
  // The upload page restores the actual form; do not cover it with the sheet.
  const handlesUpload = handlesUploadPage(location);
  const returnedJob = !handlesUpload && /^[a-f0-9]{64}$/.test(requested) ? requested : "";
  const headerActions = usePageHeaderActions();
  const [open, setOpen] = useState(false);
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    if (handlesUpload && requested) setOpen(false);
    else if (returnedJob) setOpen(true);
  }, [returnedJob, handlesUpload, requested]);
  const loader = useCallback(signal => listJobs(signal, { view: "recent" }), []);
  const resource = useJobResource(loader);
  const rows = resource.data?.items;
  const time = Math.max(clock, Date.now());
  // Hide expired outcomes here without changing the durable job or its result.
  useEffect(() => {
    const deadlines = (rows || []).filter(row => !activeStatuses.includes(row.status))
      .map(row => Date.parse(row.completed_at || row.updated_at || "") + RECENT_JOB_MS).filter(at => at > time);
    if (!deadlines.length) return undefined;
    const timer = setTimeout(() => setClock(Date.now()), Math.min(...deadlines) - time);
    return () => clearTimeout(timer);
  }, [rows, time]);
  const recent = (rows || []).filter(row => isRecentJob(row, time));
  const active = recent.filter(row => activeStatuses.includes(row.status)).length;
  const trigger = <Button type="button" variant="outline" className="gap-2 shrink-0" onClick={() => setOpen(true)} aria-label="Open background jobs" data-testid="background-jobs-open">
    Jobs{active ? ` (${active} running / queued)` : ""}
  </Button>;
  return <>
    {headerActions ? headerActions.target && createPortal(trigger, headerActions.target) : trigger}
    <Sheet open={open} onOpenChange={setOpen}><SheetContent className="w-full sm:max-w-xl overflow-y-auto">
      <SheetHeader><SheetTitle>Current jobs</SheetTitle></SheetHeader>
      <p className="text-sm text-muted-foreground my-3">Once accepted, jobs continue on the server after logout or when your device is off. Completion notifications wait in your inbox. Finished jobs stay here for 24 hours after completion; All jobs keeps earlier activity.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button type="button" variant="outline" onClick={resource.reload}>Refresh jobs</Button>
        <Link className="inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium text-primary hover:bg-accent" to={tenantPath("/jobs")} onClick={() => setOpen(false)}>All jobs</Link>
      </div>
      <ResourceStatus loading={resource.loading} error={resource.error || resource.refreshError} onRetry={resource.reload} />
      {returnedJob && <ReturnedJob key={returnedJob} identifier={returnedJob} />}
      {!resource.loading && !resource.error && !recent.length && <p className="text-sm mt-4">No jobs in progress. View All jobs for earlier activity.</p>}
      <JobList rows={recent.filter(row => row.id !== returnedJob)} />
    </SheetContent></Sheet>
  </>;
}

function ReturnedJob({ identifier }) {
  const loader = useCallback(async signal => ({ items: [await readJob(identifier, signal)] }), [identifier]);
  const resource = useJobResource(loader);
  return <section aria-label="Job from notification" className="mb-4 space-y-3">
    <h3 className="text-sm font-semibold">Job from notification</h3>
    <ResourceStatus loading={resource.loading} error={resource.error || resource.refreshError} onRetry={resource.reload} />
    <JobList rows={resource.data?.items || []} />
  </section>;
}
